import React, { Component } from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap';
import { classRowsFor, formatYearSpan } from '../../../util/marineCatalogGrouping';
import { optionsForRow, writeTargets, isWritable } from '../../../util/marineClassOptions';
import { updateBoatModel, bulkSetBoatClass, setBoatModelActive } from '../../../services/marineReferenceService';
import { numOrNull, textOrNull } from '../../../util/marineFieldCoercion';

/** Where a row's ORIGINAL value for a given wire field lives on the row shape `classRowsFor` returns. */
const ORIGINAL_FOR = {
  boat_class: (row) => row.boatClass,
  loa_ft: (row) => row.loaFt,
  beam_ft: (row) => row.beamFt,
  hull_height_in: (row) => row.hullHeightIn,
  hull_code: (row) => row.hullCode,
  year_end: (row) => row.yearEnd,
};

/**
 * `/vehicles` -> Water's Trims-modal equivalent. Class stands where trim
 * stands: a row here is one `classRowsFor` bucket, which can span multiple
 * `generationIds` when generations collapsed on identical class+dimensions.
 * Every staged edit for a row must land on every id in that bucket, or a
 * Save silently leaves half the group on the old value.
 *
 * Mirrors `newVehicles.jsx`'s `renderVehicleEditModal`: edits stage into a
 * dirty map keyed by row id (here, generation id), selection lives in a
 * `Set`, bulk-apply stages into that same map rather than calling the
 * server, and one Save flushes it.
 */
export default class MarineCraftEditModal extends Component {
  state = {
    modelName: this.props.group.model_name || '',
    // { [generationId]: { boat_class?, loa_ft?, beam_ft?, hull_height_in?, hull_code? } }
    edits: {},
    // Raw text as typed, keyed `${row.key}:${field}` — keeps a numeric input
    // stable while typing (e.g. "0" then "." then "5") instead of round-
    // tripping every keystroke through `numOrNull` and losing the cursor.
    textValues: {},
    selectedRowKeys: new Set(),
    bulkClass: '',
    saving: false,
    deactivatingKey: null,
    error: '',
    // Set on a partial-deactivate failure. The server has already changed;
    // closing must refetch, or the grid keeps rendering a row that is no
    // longer fully active. Kept separate from `isDirty()` — this is not
    // unsaved client state, it's a pending refetch the modal owes the grid.
    refetchOnClose: false,
  };

  classRows = () => classRowsFor(this.props.group);

  genLookup = () => {
    const map = new Map();
    (this.props.group.generations || []).filter(Boolean).forEach((g) => map.set(g.id, g));
    return map;
  };

  // One predicate for both "is this dirty" and "what do we send" — a
  // trailing space must never diverge between the two. model_name is part
  // of the natural key, and saved boats resolve to boat_model by STRING,
  // so a stray untrimmed write silently breaks resolution for every boat
  // on that model.
  modelNameDirty = () => this.state.modelName.trim() !== (this.props.group.model_name || '');

  isDirty = () => this.modelNameDirty() || Object.keys(this.state.edits).length > 0;

  handleClose = () => {
    if (this.isDirty() && !window.confirm('You have unsaved changes. Close anyway?')) return;
    // A partial deactivate already changed the server. Closing must refetch, or
    // the grid keeps rendering a row that is no longer fully active.
    if (this.state.refetchOnClose) this.props.onSaved();
    else this.props.onClose();
  };

  // A row's edit propagates to every generation id it covers — that is the
  // whole reason this is keyed on the row rather than on a single id.
  handleRowFieldChange = (row, field, rawValue) => {
    const value = field === 'boat_class'
      ? rawValue
      : field === 'hull_code'
        ? textOrNull(rawValue)
        : numOrNull(rawValue);
    const original = ORIGINAL_FOR[field](row) ?? null;

    this.setState((prevState) => {
      const newEdits = { ...prevState.edits };
      row.generationIds.forEach((id) => {
        const key = String(id);
        const rowEdits = { ...(newEdits[key] || {}) };
        if (value === original) delete rowEdits[field];
        else rowEdits[field] = value;
        if (Object.keys(rowEdits).length === 0) delete newEdits[key];
        else newEdits[key] = rowEdits;
      });

      const textValues = { ...prevState.textValues };
      if (field === 'boat_class') delete textValues[`${row.key}:${field}`];
      else textValues[`${row.key}:${field}`] = rawValue;

      return { edits: newEdits, textValues };
    });
  };

  // What the input should show right now: exactly what was typed, if
  // anything; otherwise whatever is staged; otherwise the original.
  fieldDisplayValue = (row, field) => {
    const textKey = `${row.key}:${field}`;
    if (textKey in this.state.textValues) return this.state.textValues[textKey];
    const genId = String(row.generationIds[0]);
    const staged = this.state.edits[genId] && this.state.edits[genId][field];
    const value = staged !== undefined ? staged : ORIGINAL_FOR[field](row);
    return value === null || value === undefined ? '' : String(value);
  };

  // The header span must follow the staged edit, not the persisted value —
  // otherwise typing 2024 leaves the header reading "2021+" above a field
  // saying 2024, and the user cannot tell which one the save will use.
  currentYearEnd = (row) => {
    const genId = String(row.generationIds[0]);
    const staged = this.state.edits[genId] && this.state.edits[genId].year_end;
    return staged !== undefined ? staged : row.yearEnd;
  };

  currentClassSlug = (row) => {
    const genId = String(row.generationIds[0]);
    const staged = this.state.edits[genId] && this.state.edits[genId].boat_class;
    return staged !== undefined ? staged : row.boatClass;
  };

  handleToggleRowSelect = (rowKey) => {
    this.setState((prevState) => {
      const next = new Set(prevState.selectedRowKeys);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return { selectedRowKeys: next };
    });
  };

  handleSelectAllRows = () => {
    const rows = this.classRows();
    const allKeys = rows.map((r) => r.key);
    const allSelected = allKeys.length > 0 && allKeys.every((k) => this.state.selectedRowKeys.has(k));
    this.setState({ selectedRowKeys: allSelected ? new Set() : new Set(allKeys) });
  };

  // Stages only — never calls the server. One Save flushes everything staged.
  handleBulkApply = () => {
    const { selectedRowKeys, bulkClass } = this.state;
    if (selectedRowKeys.size === 0 || !bulkClass) return;

    this.setState((prevState) => {
      const newEdits = { ...prevState.edits };
      this.classRows().forEach((row) => {
        if (!selectedRowKeys.has(row.key)) return;
        row.generationIds.forEach((id) => {
          const key = String(id);
          const rowEdits = { ...(newEdits[key] || {}) };
          if (bulkClass === row.boatClass) delete rowEdits.boat_class;
          else rowEdits.boat_class = bulkClass;
          if (Object.keys(rowEdits).length === 0) delete newEdits[key];
          else newEdits[key] = rowEdits;
        });
      });
      return { edits: newEdits };
    });
  };

  handleDeactivate = async (row) => {
    // One confirm, one decision: deactivating throws away any staged edits
    // (unlike Close, this path never asked before) so the warning is folded
    // into the same dialog rather than stacking a second confirm on top.
    const dirtyWarning = this.isDirty()
      ? ' This will discard your unsaved edits in this modal.'
      : '';
    if (!window.confirm(`Deactivate "${row.label}"? It will no longer be offered on new estimates.${dirtyWarning}`)) return;
    this.setState({ deactivatingKey: row.key, error: '' });

    // Deactivation can't converge the way a metadata save can — a
    // generation blocked by attached boats stays blocked. Track what
    // actually succeeded so a partial failure is reported by name instead
    // of hiding behind a generic message.
    const succeeded = [];
    try {
      for (const id of row.generationIds) {
        await setBoatModelActive(id, false);
        succeeded.push(id);
      }
      this.props.onSaved();
    } catch (error) {
      if (succeeded.length > 0) {
        // Partial failure: some generations in this row are now deactivated
        // server-side and some are not. Two things must both happen, not
        // just one: the user must actually SEE which ones (closing
        // immediately, even after setState, unmounts this component in the
        // same synchronous tick and the banner is never painted), and the
        // grid must eventually stop rendering the row as fully intact. So
        // the modal stays open with the banner now, and `handleClose` below
        // refetches when it eventually closes instead of this refetching
        // right away.
        const total = row.generationIds.length;
        this.setState({
          deactivatingKey: null,
          refetchOnClose: true,
          error: `Deactivated ${succeeded.length} of ${total} generation${total !== 1 ? 's' : ''} for "${row.label}": ${error.message}`,
        });
      } else {
        // The server's 409 carries an attached-boat count in this message —
        // surface it verbatim, not as a generic failure.
        this.setState({ deactivatingKey: null, error: error.message });
      }
    }
  };

  // Verbatim per the Phase 2 Task 3 brief — the trickiest routing logic.
  // Refuses a non-canonical class locally rather than letting the server
  // 400, routes a class-only change through the atomic bulk endpoint
  // (grouped by target slug), and routes anything with a dimension change
  // through the per-row PUT, which is the only endpoint that carries
  // dimensions. Sequential, not Promise.all: a group holds at most two
  // generations, so there is nothing to win from parallelism, and a partial
  // failure is easier to reason about — and to report — in order.
  handleSave = async () => {
    const { edits } = this.state;
    const ids = Object.keys(edits);
    // A rename-only edit has an empty `edits` map but is still dirty — the
    // guard must not return before the rename branch below ever runs, or
    // Save renders enabled and does nothing.
    if (ids.length === 0 && !this.modelNameDirty()) return;

    // Compare AND write the trimmed value, via the same predicate `isDirty`
    // uses. model_name is part of the natural key (make_id, model_name,
    // year_start) and a saved boat resolves to boat_model by STRING, not by
    // FK — an untrimmed or blank write silently breaks that resolution for
    // every boat on this model, with no server-side guard to catch it.
    // Mirror the server's year_end contract locally — YEAR_END_BEFORE_START and
    // INVALID_YEAR_END are both cheap to catch here, and naming the row beats a
    // round-trip that can only say which id failed.
    for (const row of this.classRows()) {
      const staged = this.state.edits[String(row.generationIds[0])];
      if (!staged || !('year_end' in staged)) continue;
      const ye = staged.year_end;
      if (ye === null) continue;
      if (!Number.isInteger(ye) || ye < 1900 || ye > 2100) {
        this.setState({ error: `"${row.label}": end year must be a four-digit year, or blank while still in production.` });
        return;
      }
      if (row.yearStart != null && ye < row.yearStart) {
        this.setState({ error: `"${row.label}": end year ${ye} is before the generation's start year ${row.yearStart}.` });
        return;
      }
    }

    const trimmedModelName = this.state.modelName.trim();
    if (this.modelNameDirty() && trimmedModelName === '') {
      this.setState({ error: 'Model name cannot be blank.' });
      return;
    }

    const bad = ids.find((id) => 'boat_class' in edits[id] && !isWritable(this.props.classes, edits[id].boat_class));
    if (bad) {
      this.setState({ error: `"${edits[bad].boat_class}" is not a class that can be saved. Pick another.` });
      return;
    }

    const classOnly = ids.filter((id) => Object.keys(edits[id]).length === 1 && 'boat_class' in edits[id]);
    const byClass = classOnly.reduce((acc, id) => {
      const slug = edits[id].boat_class;
      (acc[slug] = acc[slug] || []).push(Number(id));
      return acc;
    }, {});

    this.setState({ saving: true, error: '' });
    try {
      for (const [slug, batchIds] of Object.entries(byClass)) {
        if (batchIds.length > 1) await bulkSetBoatClass(batchIds, slug);
        else await updateBoatModel(batchIds[0], { boat_class: slug });
      }
      const singles = ids.filter((id) => !classOnly.includes(id));
      for (const id of singles) {
        await updateBoatModel(id, edits[id]);
      }
      if (this.modelNameDirty()) {
        for (const row of classRowsFor(this.props.group)) {
          for (const genId of row.generationIds) {
            await updateBoatModel(genId, { model_name: trimmedModelName });
          }
        }
      }
      this.props.onSaved();
    } catch (error) {
      this.setState({ saving: false, error: error.message });
    }
  };

  renderClassCell(row) {
    const { canEdit, classes } = this.props;
    if (!canEdit) return row.label;

    const currentSlug = this.currentClassSlug(row);
    const options = optionsForRow(classes, currentSlug);
    return (
      <Form.Control
        as="select"
        size="sm"
        value={currentSlug || ''}
        onChange={(e) => this.handleRowFieldChange(row, 'boat_class', e.target.value)}
      >
        {!currentSlug && <option value="">Unclassified</option>}
        {options.map((opt) => (
          <option key={opt.slug} value={opt.slug} disabled={opt.disabled}>{opt.label}</option>
        ))}
      </Form.Control>
    );
  }

  /**
   * End Year — the one year field that is safe to edit.
   *
   * `year_start` is deliberately absent and must stay that way: it is part of
   * UNIQUE (make_id, model_name, year_start) AND `external_id` is
   * slugify(make-model-year_start), which is the string saved boats resolve
   * through. `year_end` is in neither.
   *
   * Blank is not "unknown" — it is the CORRECT state for a model still in
   * production, and the server stores it as null.
   *
   * Disabled on a collapsed row. When two generations merged into one class
   * row (identical class and dimensions), `row.yearEnd` is the union of their
   * spans; writing one value back to both would set 2018-2021 and 2022-2024
   * to the same end year and make them overlap.
   */
  renderYearEndCell(row) {
    const spansMultiple = row.generationIds.length > 1;
    if (!this.props.canEdit || spansMultiple) {
      const raw = row.yearEnd;
      return (
        <div>
          <span className={raw == null ? 'text-muted' : ''}>
            {raw == null ? 'In production' : raw}
          </span>
          {spansMultiple && this.props.canEdit && (
            <Form.Text className="text-muted">
              Not editable here — this row covers {row.generationIds.length} generations, and one
              end year would overlap their spans. Split them server-side first.
            </Form.Text>
          )}
        </div>
      );
    }
    return (
      <React.Fragment>
        <Form.Control
          type="number"
          step="1"
          size="sm"
          placeholder="In production"
          value={this.fieldDisplayValue(row, 'year_end')}
          onChange={(e) => this.handleRowFieldChange(row, 'year_end', e.target.value)}
        />
        <Form.Text className="text-muted">
          Leave blank while the model is still in production. Set it to the last production year
          to close the generation out.
        </Form.Text>
      </React.Fragment>
    );
  }

  renderNumberCell(row, field, step) {
    if (!this.props.canEdit) {
      const raw = ORIGINAL_FOR[field](row);
      return raw === null || raw === undefined ? <span className="text-muted">&mdash;</span> : raw;
    }
    return (
      <Form.Control
        type="number"
        step={step}
        size="sm"
        value={this.fieldDisplayValue(row, field)}
        onChange={(e) => this.handleRowFieldChange(row, field, e.target.value)}
      />
    );
  }

  renderTextCell(row, field) {
    if (!this.props.canEdit) {
      const raw = ORIGINAL_FOR[field](row);
      return raw || <span className="text-muted">&mdash;</span>;
    }
    return (
      <Form.Control
        type="text"
        size="sm"
        value={this.fieldDisplayValue(row, field)}
        onChange={(e) => this.handleRowFieldChange(row, field, e.target.value)}
      />
    );
  }

  renderProvenance(row) {
    const gen = this.genLookup().get(row.generationIds[0]) || {};
    return (
      <small className="text-muted d-block" style={{ lineHeight: 1.3 }}>
        <div>dim: {gen.dim_flag || '—'}</div>
        <div>hull src: {gen.hull_height_source || '—'}</div>
        <div>conf: {gen.source_confidence ?? '—'}</div>
      </small>
    );
  }

  render() {
    const { group, classes, canEdit } = this.props;
    const { modelName, selectedRowKeys, bulkClass, saving, deactivatingKey, error, edits } = this.state;
    const rows = this.classRows();
    const allKeys = rows.map((r) => r.key);
    const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedRowKeys.has(k));
    const dirtyCount = Object.keys(edits).length;

    return (
      <Modal show onHide={this.handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{canEdit ? 'Edit: ' : ''}{group.make_name} {group.model_name}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="mb-4 pb-3 border-bottom">
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label><strong>Make</strong></Form.Label>
                  <div title="Make names cannot be edited here.">{group.make_name}</div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label><strong>Model</strong></Form.Label>
                  {canEdit ? (
                    <Form.Control
                      type="text"
                      value={modelName}
                      onChange={(e) => this.setState({ modelName: e.target.value })}
                    />
                  ) : group.model_name}
                </Form.Group>
              </Col>
            </Row>
          </div>

          {canEdit && rows.length > 1 && (
            <div className="mb-3 p-2 bg-light rounded">
              <Row className="align-items-center">
                <Col xs="auto"><small><strong>Bulk:</strong></small></Col>
                <Col md={4}>
                  <Form.Control
                    as="select"
                    size="sm"
                    value={bulkClass}
                    onChange={(e) => this.setState({ bulkClass: e.target.value })}
                  >
                    <option value="">Class...</option>
                    {writeTargets(classes).map((c) => (
                      <option key={c.slug} value={c.slug}>{c.label}</option>
                    ))}
                  </Form.Control>
                </Col>
                <Col xs="auto">
                  <button
                    className="jv-btn jv-btn-secondary jv-btn-sm"
                    onClick={this.handleBulkApply}
                    disabled={selectedRowKeys.size === 0 || !bulkClass}
                  >
                    Apply to Selected
                  </button>
                </Col>
                <Col xs="auto">
                  {/* Moved out of the old table header, which no longer exists. */}
                  <Form.Check
                    type="checkbox"
                    label="Select all"
                    checked={allSelected}
                    onChange={this.handleSelectAllRows}
                  />
                </Col>
              </Row>
            </div>
          )}

          {rows.map((row) => {
            const isDirty = row.generationIds.some((id) => edits[String(id)]);
            return (
              <div
                key={row.key}
                className="border rounded p-3 mb-3"
                style={isDirty ? { backgroundColor: 'var(--color-warning-light)' } : {}}
              >
                {/* Card header — identity, provenance and the row-level action.
                    Provenance sits here rather than beside a field because it
                    describes the whole generation, not one number. */}
                <div className="d-flex align-items-start justify-content-between mb-3">
                  <div className="d-flex align-items-center">
                    {canEdit && rows.length > 1 && (
                      <Form.Check
                        type="checkbox"
                        className="mr-3"
                        checked={selectedRowKeys.has(row.key)}
                        onChange={() => this.handleToggleRowSelect(row.key)}
                      />
                    )}
                    <div>
                      <strong>{row.label}</strong>
                      <small className="text-muted d-block">
                        {formatYearSpan(row.yearStart, this.currentYearEnd(row))}
                        {row.generationIds.length > 1 && ` · ${row.generationIds.length} generations`}
                      </small>
                    </div>
                  </div>
                  <div className="d-flex align-items-start">
                    {this.renderProvenance(row)}
                    {canEdit && (
                      <button
                        className="jv-btn-icon ml-3"
                        onClick={() => this.handleDeactivate(row)}
                        disabled={saving || !!deactivatingKey}
                        title="Deactivate this class — it will no longer be offered on new estimates"
                        style={{ color: 'var(--color-danger)' }}
                      >
                        {deactivatingKey === row.key
                          ? <i className="fa-sharp fa-regular fa-spinner fa-spin" />
                          : <i className="fa-sharp fa-regular fa-ban" />}
                      </button>
                    )}
                  </div>
                </div>

                {/* Fields stacked full-width rather than as table columns: at
                    seven columns the inputs were too narrow to read a decimal
                    in, and a model with two generations doubled the crowding. */}
                <Form.Group className="mb-3">
                  <Form.Label><strong>Class</strong></Form.Label>
                  {this.renderClassCell(row)}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>End Year</strong></Form.Label>
                  {this.renderYearEndCell(row)}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>LOA (ft)</strong></Form.Label>
                  {this.renderNumberCell(row, 'loa_ft', '0.01')}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Beam (ft)</strong></Form.Label>
                  {this.renderNumberCell(row, 'beam_ft', '0.01')}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Freeboard (in)</strong></Form.Label>
                  {this.renderNumberCell(row, 'hull_height_in', '1')}
                  <Form.Text className="text-muted">
                    Blank means not yet measured — it is never stored as zero.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Label><strong>Hull Material</strong></Form.Label>
                  {this.renderTextCell(row, 'hull_code')}
                </Form.Group>
              </div>
            );
          })}

        </Modal.Body>

        <Modal.Footer>
          {canEdit && this.isDirty() && (
            <button className="jv-btn jv-btn-primary" onClick={this.handleSave} disabled={saving || !!deactivatingKey}>
              {saving ? (
                <>
                  <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2" />
                  Saving...
                </>
              ) : `Save Changes${dirtyCount ? ` (${dirtyCount})` : ''}`}
            </button>
          )}
          <button className="jv-btn jv-btn-tertiary" onClick={this.handleClose}>Close</button>
        </Modal.Footer>
      </Modal>
    );
  }
}
